// Overview.js (Updated for API Fetch and Trial Days)
import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from "../../../utils/axiosInstance"; // Import axios
import backendURL from '../../../config'; // Import your backend URL config
import { Box, Card, Button as MuiButton, CardContent,Grid, Typography, CircularProgress, Alert, Chip } from '@mui/material';

// Helper functions (assuming they are defined elsewhere or keep them here)
const formatCurrency = (amount, currencyCode = 'usd') => {
    // Keep implementation
    if (typeof amount !== 'number') return '';
    try {
         return new Intl.NumberFormat('en-US', {
             style: 'currency',
             currency: currencyCode?.toUpperCase() || 'USD',
         }).format(amount);
    } catch(e) {
         console.warn("Currency formatting failed:", e);
         return `$${amount.toFixed(2)}`;
    }
};

const formatDate = (dateString) => {
    // Keep implementation
    if (!dateString) return 'N/A';
    try {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric'
        });
    } catch (e) {
        console.warn("Date formatting failed:", e);
        return dateString;
    }
};

// Helper to format large numbers (like tokens)
const formatLargeNumber = (num) => {
    if (num == null) return '0';
    if (typeof num == 'boolean') return (num) ? "Yes" : "No" ;
    if (num === -1) return 'Unlimited'; // Handle unlimited case
    if (num >= 1e9) return (num / 1e9).toFixed(1) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(1) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K';
    return num.toString();
};

// NEW Helper function to calculate days remaining
const calculateDaysRemaining = (endDateString) => {
    if (!endDateString) return null;
    try {
        const endDate = new Date(endDateString);
        const now = new Date();
        // Check if the end date is valid and in the future
        if (isNaN(endDate.getTime()) || endDate <= now) {
            return 0; // Or null if you prefer not to show "0 days"
        }
        const diffTime = endDate.getTime() - now.getTime();
        // Use Math.ceil to count the current day if there's any time left
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    } catch (e) {
        console.error("Error calculating days remaining:", e);
        return null; // Indicate error or invalid date
    }
};


const Overview = () => {
    // --- STATE ---
    const [subscription, setSubscription] = useState(null); // Initialize as null
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCanceling, setIsCanceling] = useState(false);
    const [cancelError, setCancelError] = useState(null);
    // No need for showCancelConfirm state if directly cancelling
    // const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    const fetchSubscriptionOverview = useCallback(async (showLoadingSpinner = true) => {
        if (showLoadingSpinner) {
            setLoading(true);
        }
        setError(null);
        

        try {
            const response = await axiosInstance.get(`/subscriptions/current`);
            console.log("Refreshed Subscription API Response:", response.data);
            if (response.data) {
                setSubscription(response.data);
                const userStr = localStorage.getItem('user');
                if (userStr) {
                    const prevUser = JSON.parse(userStr);
                    let updatedUser = {
                        ...prevUser,
                        active_team: {
                        ...prevUser.active_team,
                        subscription: response.data,
                        has_used_trial: (response.data && response.data?.plan?.price > 0) ? true : false
                        },
                        teams: prevUser.teams.map(team =>
                        team.team_id === prevUser.active_team.team_id
                            ? { 
                                ...team,
                                subscription: response.data,
                                has_used_trial: (response.data && response.data?.plan?.price > 0) ? true : false}
                            : team
                        ),
                    };
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                    console.log("Local storage user subscription updated.");
                } else {
                     console.warn("User data not found in local storage for update.");
                }
            } else if (response.data && response.data.status === 'not_found' && !response.data.data) {
                setSubscription(null); // Handle case where API explicitly says not found
            }
            else {
                // Should not happen if API is consistent, but good practice
                setError("Received incomplete or unexpected subscription data from server.");
                setSubscription(null); // Set to null if data structure is wrong
            }
        } catch (err) {
             console.error("Error fetching subscription overview:", err);
            if (err.response?.status === 401) {
                 setError("Your session may have expired. Please log in again.");
                 setSubscription(null); // Ensure no stale data on auth error
             } else if (err.response?.status === 404) {
                  // If 404 means *no subscription*, set state to null
                  setSubscription(null);
                  setError(null); // Clear any previous error, 404 is a valid state here
             } else {
                 if (!isCanceling) {
                     setError(err.response?.data?.message || err.message || "Failed to load subscription details.");
                 }
                 setSubscription(null); // Ensure no stale data on other errors
             }
        } finally {
            if (showLoadingSpinner) setLoading(false);
        }
    // Removed isCanceling dependency - fetch should happen regardless of cancelling state
    // If cancellation logic requires a refresh, it calls fetchSubscriptionOverview directly
    }, []); // Empty dependency array is generally correct for fetch on mount/refresh

    useEffect(() => {
        fetchSubscriptionOverview();
    }, [fetchSubscriptionOverview]); // Run fetch on mount

    const handleCancelSubscription = async () => {
        // Removed setShowCancelConfirm(false);
        setIsCanceling(true);
        setCancelError(null);

        try {
            const response = await axiosInstance.delete(`/subscriptions/current`);

            if (response.data) {
                // Refresh the data *without* the loading spinner showing again
                await fetchSubscriptionOverview(false);
                // Update local storage immediately after successful cancel + refresh
                const userStr = localStorage.getItem('user');
                if (userStr && subscription) { 
                    const prevUser = JSON.parse(userStr);
                    let updatedUser = {
                        ...prevUser,
                        active_team: {
                        ...prevUser.active_team,
                        subscription: response.data,
                        has_used_trial: (response.data && response.data?.plan?.price > 0) ? true : false
                        },
                        teams: prevUser.teams.map(team =>
                        team.team_id === prevUser.active_team.team_id
                            ? { 
                                ...team,
                                subscription: response.data,
                                has_used_trial: (response.data && response.data?.plan?.price > 0) ? true : false}
                            : team
                        ),
                    };
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                }

            } else {
                // Attempt to get a specific message, fallback otherwise
                throw new Error(response.data?.message || "Cancellation request failed with unexpected response.");
            }
        } catch (err) {
            console.error("Error canceling subscription:", err);
            // Prioritize backend error message if available
            setCancelError(err.response?.data?.message || err.message || "Failed to cancel subscription.");
            // Don't refresh on error, keep showing the previous state
        } finally {
            setIsCanceling(false);
        }
    };

    // --- RENDER LOGIC ---
    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
    }

    // Show error *only* if it's not a "not found" scenario (which is handled by !subscription)
    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    // Handle case where API returned 404 or fetch failed and set subscription to null
    if (!subscription) {
         return (
             <Card variant="outlined">
                 <CardContent>
                     <Typography variant="h6" gutterBottom>Current Plan</Typography>
                     <Typography color="text.secondary">
                         You do not have an active subscription.
                     </Typography>
                     {/* Optional: Button/Link to Pricing Page */}
                     {/* <Button component={RouterLink} to="/pricing" variant="contained" sx={{ mt: 2 }}>Choose a Plan</Button> */}
                 </CardContent>
             </Card>
         );
    }

    // --- Destructure data safely ---
    const {
        plan = { display_name: 'Unknown Plan', price: 0, currency: 'usd', interval: null, limits: {} },
        status = 'unknown', // Default to 'unknown' if not provided
        // Top-level `interval` is the cycle the subscription is ACTUALLY on
        // (monthly/yearly), independent of plan.interval. Falls back to the
        // plan's nominal cadence for older API responses that don't include it.
        interval: subscriptionInterval,
        current_period_end,
        trial_ends_at,
        canceled_at,
        access_ends_at,
        usage = {},
        credit_balance
    } = subscription;
    // Cycle the customer actually pays on -- 'year' or 'month'.
    const billingCycle = subscriptionInterval || plan.interval || 'month';
    // Yearly subs are billed plan.price * 12 with a 10% discount; the
    // backend computes the post-discount total into plan.effective_price.
    // We display that as the "real" price so the customer never sees the
    // monthly base juxtaposed with a yearly billing cycle (confusing).
    const monthlyBase = Number(plan.price) || 0;
    const isYearly = billingCycle === 'year';
    const listPrice = isYearly ? monthlyBase * 12 : monthlyBase;
    const effectivePrice = (typeof plan.effective_price === 'number')
        ? plan.effective_price
        : listPrice;
    const discountPct = Number(plan.discount_value) || 0;
    const monthlyEquivalent = isYearly && effectivePrice > 0 ? effectivePrice / 12 : null;

    // --- Calculate Trial Days Remaining ---
    let trialDaysRemaining = null;
    if (status === 'trialing' && trial_ends_at) {
        trialDaysRemaining = calculateDaysRemaining(trial_ends_at);
    }

    // Determine effective end date based on status
    // const endDate = status === 'trialing' ? trial_ends_at : current_period_end; // Not currently used directly
    const canCancel = !canceled_at && status !== 'freemium' && status !== 'canceled' && status !== 'incomplete_expired' && status !== 'past_due'; // Add other non-cancelable statuses


    return (
        <Card variant="outlined">
            <CardContent>
                <Typography variant="h5" component="div" gutterBottom>
                    Subscription Overview
                </Typography>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" component="div"> {/* Use component="div" for better structure */}
                        Current Plan: {plan.display_name || 'N/A'}
                        {status && (
                            <Chip
                                label={status}
                                size="small"
                                sx={{ ml: 1, textTransform: 'capitalize', verticalAlign: 'middle' }} // Align chip vertically
                                color={
                                    status === 'active' ? 'success'
                                    : status === 'trialing' ? 'primary'
                                    : status === 'canceled' || status === 'past_due' ? 'warning'
                                    : 'default'
                                }
                            />
                        )}
                        {/* --- Display Trial Days Remaining --- */}
                        {status === 'trialing' && trialDaysRemaining !== null && trialDaysRemaining > 0 && (
                             <Typography component="span" variant="body2" sx={{ ml: 0.5, color: 'red', verticalAlign: 'middle' }}>
                                 ({trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''} left)
                             </Typography>
                        )}
                         {status === 'trialing' && trialDaysRemaining !== null && trialDaysRemaining <= 0 && (
                             <Typography component="span" variant="body2" sx={{ ml: 0.5, color: 'red', verticalAlign: 'middle' }}>
                                 (Trial ended)
                             </Typography>
                        )}
                    </Typography>
                    {/* Show the price the customer is ACTUALLY billed for this cycle.
                        For yearly subs we display the post-discount yearly total + a
                        "(was $X · save Y%)" line, NOT the monthly base, so the user
                        doesn't read "$250 / month" on a yearly subscription. */}
                    {monthlyBase > 0 ? (
                        <>
                            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                                {`${formatCurrency(effectivePrice, plan.currency)} / ${billingCycle}`}
                                {isYearly && discountPct > 0 && listPrice > effectivePrice && (
                                    <Typography component="span" variant="body2"
                                                sx={{ ml: 1, color: 'text.disabled', textDecoration: 'line-through' }}>
                                        {formatCurrency(listPrice, plan.currency)}
                                    </Typography>
                                )}
                                {isYearly && discountPct > 0 && (
                                    <Chip
                                        size="small" color="success"
                                        label={`Save ${Math.round(discountPct)}%`}
                                        sx={{ ml: 1, height: 18, fontSize: '0.7rem', fontWeight: 600 }}
                                    />
                                )}
                            </Typography>
                            {monthlyEquivalent && (
                                <Typography variant="caption" color="text.secondary">
                                    Equivalent to {formatCurrency(monthlyEquivalent, plan.currency)} / month, billed yearly
                                </Typography>
                            )}
                        </>
                    ) : (
                        <Typography color="text.secondary" sx={{ mt: 0.5 }}>Free Plan</Typography>
                    )}
                    {current_period_end && status !== 'trialing' && !canceled_at && ( // Only show next billing if active and not canceled
                        <Typography color="text.secondary">
                            Next Billing Date: {formatDate(current_period_end)}
                        </Typography>
                    )}
                     {/* Show Trial End Date explicitly */}
                     {status === 'trialing' && trial_ends_at && (
                        <Typography color="text.secondary">
                            Trial Ends: {formatDate(trial_ends_at)}
                        </Typography>
                    )}
                    <Typography color="text.secondary">
                        Credit Balance: {formatCurrency(credit_balance || 0.00, plan.currency || 'usd')}
                    </Typography>

                    {canceled_at && (
                        <Alert severity="warning" sx={{ mt: 2, mb: 1 }}> {/* Adjusted margin */}
                            <Typography variant="body2">
                                Subscription canceled on {formatDate(canceled_at)}.
                                {access_ends_at && new Date(access_ends_at) > new Date() ? (
                                    ` Access ends on ${formatDate(access_ends_at)}.`
                                ) : (
                                    ` Access has ended.`
                                )}
                            </Typography>
                        </Alert>
                    )}

                    {/* --- Action Buttons --- */}
                    <Box sx={{ mt: canceled_at ? 1 : 3, pt: 2, borderTop: '1px solid #eee', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {/* Example: Button to Manage Billing Portal (Add link later) */}
                        {/* {status !== 'freemium' && status !== 'canceled' && (
                            <MuiButton variant="contained" disabled>Manage Billing</MuiButton>
                        )} */}

                        {/* Cancel Subscription Button - Conditional */}
                        {canCancel && (
                            <MuiButton
                                variant="outlined"
                                color="error"
                                onClick={handleCancelSubscription}
                                disabled={isCanceling} // Disable button during API call
                                startIcon={isCanceling ? <CircularProgress size={20} color="inherit"/> : null}
                            >
                                {isCanceling ? 'Canceling...' : 'Cancel Subscription'}
                            </MuiButton>
                        )}
                        {/* Optionally add Reactivate or Change Plan buttons if applicable */}
                        {/* {canceled_at && access_ends_at && new Date(access_ends_at) > new Date() && ( ... )} */}
                        {/* {status === 'freemium' && ( <Button component={RouterLink} to="/pricing">Upgrade Plan</Button> )} */}
                    </Box>

                    {/* --- Cancellation Error Display --- */}
                    {cancelError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {cancelError}
                        </Alert>
                    )}
                </Box>

                {/* --- Usage Section --- */}
                {(plan.limits && Object.keys(plan.limits).length > 0) && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" gutterBottom>Current Period Usage</Typography>
                         {/* Display usage only if not freemium or if usage object exists */}
                        {(status !== 'freemium' && usage && Object.keys(usage).length > 0) ? (
                            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid #ccc' }}>
                                        <th style={{ textAlign: 'left', padding: '8px', textTransform: 'capitalize' }}>Resource</th>
                                        <th style={{ textAlign: 'right', padding: '8px' }}>Used</th>
                                        <th style={{ textAlign: 'right', padding: '8px' }}>Limit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.keys(plan.limits).map((key) => (
                                        <tr key={key} style={{ borderBottom: '1px solid #eee' }}>
                                            <td style={{ padding: '8px', textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</td>
                                            <td style={{ padding: '8px', textAlign: 'right' }}>
                                                {/* Use usage[key] if available, otherwise 0 */}
                                                {formatLargeNumber(usage?.[key] ?? 0)}
                                            </td>
                                            <td style={{ padding: '8px', textAlign: 'right' }}>
                                                {plan.limits[key] === -1 ? 'Unlimited' : formatLargeNumber(plan.limits[key])}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : status === 'freemium' ? (
                             <Typography color="text.secondary" sx={{mt: 1}}>Usage tracking applies to paid plans.</Typography>
                        ) : (
                             <Typography color="text.secondary" sx={{mt: 1}}>Usage data not available for this period.</Typography>
                        ) }
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default Overview;